import React, { useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { saGetOrganizations, saUpdateOrgPlan, saUpdateOrgStatus } from '../../api/api';
import StyledSelect from '../../components/StyledSelect';
import { ActionIconButton, SuperAdminPanel } from '../components/SuperAdminUi';

export default function SuperAdminOrganizations() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryOrgId = Number(searchParams.get('orgId') || 0);
  const statusFilterOptions = [
    { value: '', label: 'All statuses' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'pending', label: 'Pending' },
  ];
  const statusOptions = statusFilterOptions.filter((item) => item.value !== '');
  const planOptions = [
    { value: 'free', label: 'Free' },
    { value: 'starter', label: 'Starter' },
    { value: 'professional', label: 'Professional' },
    { value: 'enterprise', label: 'Enterprise' },
  ];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [editOrg, setEditOrg] = useState(null);
  const [editStatus, setEditStatus] = useState('active');
  const [editPlan, setEditPlan] = useState('free');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await saGetOrganizations({ page: 1, limit: 100, search: search || undefined, status: status || undefined });
      setRows(res?.data?.data?.organizations || []);
    } catch (e) {
      setRows([]);
      setError(e?.response?.data?.message || 'Failed to load organizations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!queryOrgId || rows.length === 0) return;
    const target = rows.find((row) => Number(row.id) === queryOrgId);
    if (target) {
      openEditPanel(target);
    }
  }, [queryOrgId, rows]);

  const openEditPanel = (org) => {
    setEditOrg(org);
    setEditStatus(org?.status || 'active');
    setEditPlan(org?.subscription_plan || 'free');
    setEditError('');
  };

  const closeEditPanel = () => {
    if (savingEdit) return;
    setEditOrg(null);
    setEditError('');
  };

  const saveOrgChanges = async () => {
    if (!editOrg) return;
    setSavingEdit(true);
    setEditError('');
    try {
      await Promise.all([
        saUpdateOrgStatus(editOrg.id, { status: editStatus }),
        saUpdateOrgPlan(editOrg.id, { subscription_plan: editPlan }),
      ]);
      await load();
      setEditOrg(null);
    } catch (e) {
      setEditError(e?.response?.data?.message || 'Failed to save organization changes.');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="superadmin-title text-3xl lg:text-4xl font-bold">Organizations</h1>
        <p className="text-slate-500 mt-1">Manage tenant lifecycle, plan, and organization status.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Use Shared Create Organization Registration
          </button>
          <button
            type="button"
            onClick={() => navigate('/join')}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Use Shared Invitation Join Flow
          </button>
        </div>
      </div>

      <div className="superadmin-glass rounded-2xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search org by name/code/email"
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none"
          />
          <StyledSelect
            value={status}
            onChange={setStatus}
            options={statusFilterOptions}
            placeholder="Filter by status"
            searchable
          />
          <button onClick={load} className="px-4 py-2 rounded-xl text-white text-sm font-semibold" style={{ background: '#c44569' }}>
            Apply Filters
          </button>
        </div>
      </div>

      <div className="superadmin-glass rounded-2xl p-4">
        {loading && <p className="text-sm text-slate-500">Loading organizations...</p>}
        {!loading && error && <p className="text-sm text-rose-600">{error}</p>}

        {!loading && !error && (
          <div className="overflow-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-2">Organization</th>
                  <th>Code</th>
                  <th>Owner Email</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Members</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-3">
                      <p className="font-semibold text-slate-800">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.slug}</p>
                    </td>
                    <td>{r.code}</td>
                    <td>{r.email || 'n/a'}</td>
                    <td>
                      <span className="px-2 py-1 rounded-full text-xs font-semibold" style={{
                        background: r.status === 'active' ? '#dcfce7' : r.status === 'suspended' ? '#fee2e2' : '#f1f5f9',
                        color: r.status === 'active' ? '#166534' : r.status === 'suspended' ? '#991b1b' : '#334155',
                      }}>
                        {r.status}
                      </span>
                    </td>
                    <td>{r.subscription_plan || 'free'}</td>
                    <td>{r.member_count ?? '-'}</td>
                    <td className="text-right">
                      <ActionIconButton icon={Pencil} label="Edit organization" tone="edit" onClick={() => openEditPanel(r)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SuperAdminPanel
        open={Boolean(editOrg)}
        onClose={closeEditPanel}
        title={editOrg ? `Edit ${editOrg.name}` : 'Edit organization'}
        subtitle={editOrg ? `${editOrg.code || 'n/a'} • ${editOrg.email || 'no owner email'}` : ''}
        footer={(
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              className="ems-btn-ghost"
              onClick={closeEditPanel}
              disabled={savingEdit}
            >
              Cancel
            </button>
            <button
              type="button"
              className="ems-btn-primary"
              onClick={saveOrgChanges}
              disabled={savingEdit}
            >
              {savingEdit ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        )}
      >
        <div className="space-y-4">
          {editError ? <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{editError}</p> : null}

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Organization status</label>
            <StyledSelect
              value={editStatus}
              onChange={setEditStatus}
              options={statusOptions}
              placeholder="Select status"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Subscription plan</label>
            <StyledSelect
              value={editPlan}
              onChange={setEditPlan}
              options={planOptions}
              placeholder="Select plan"
            />
          </div>
        </div>
      </SuperAdminPanel>
    </div>
  );
}
